import { useState } from 'react'
import type { SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { showError } from '@/lib/utils/notifications'
import {
  getCityMap,
  getPropertiesByStateCity,
} from '@/features/application/services/applicationService'
import { getStates, type State } from '@/lib/api/statesService'
import { dropJunkCityNames } from '@/lib/utils/cityFilterDisplay'
import type { PropertyTabPropertyRecord } from '../types/tabs'
import type { ApplyPropertyFn } from '../types/hooks'

export function usePropertyTabAdvanceSearch({
  applySelectedProperty,
  setPropertyOptions,
  setSelectedPropertyData,
}: {
  applySelectedProperty: ApplyPropertyFn
  setPropertyOptions: React.Dispatch<React.SetStateAction<SearchableSelectOption[]>>
  setSelectedPropertyData: React.Dispatch<
    React.SetStateAction<PropertyTabPropertyRecord | null>
  >
}) {
  const [isAdvanceSearchOpen, setIsAdvanceSearchOpen] = useState(false)
  const [advanceSearchSelectedId, setAdvanceSearchSelectedId] = useState<string>('')
  const [advanceSearchState, setAdvanceSearchState] = useState('')
  const [advanceSearchCity, setAdvanceSearchCity] = useState('')
  const [advanceSearchStates, setAdvanceSearchStates] = useState<State[]>([])
  const [advanceSearchLoadingStates, setAdvanceSearchLoadingStates] = useState(false)
  const [advanceSearchCityMap, setAdvanceSearchCityMap] = useState<Record<string, string[]>>({})
  const [advanceSearchCities, setAdvanceSearchCities] = useState<SearchableSelectOption[]>([])
  const [advanceSearchProperties, setAdvanceSearchProperties] = useState<SearchableSelectOption[]>([])
  const [advanceSearchLoadingProperties, setAdvanceSearchLoadingProperties] = useState(false)

  const loadAdvanceSearchReferenceData = async (): Promise<Record<string, string[]>> => {
    if (advanceSearchStates.length > 0 && Object.keys(advanceSearchCityMap).length > 0) {
      return advanceSearchCityMap
    }
    setAdvanceSearchLoadingStates(true)
    try {
      const [states, cityMap] = await Promise.all([
        getStates({ zip_flag: '1' }),
        getCityMap(),
      ])
      setAdvanceSearchStates(states)
      setAdvanceSearchCityMap(cityMap)
      return cityMap
    } catch (e) {
      showError((e as { message?: string })?.message || 'Failed to load reference data.')
      return {}
    } finally {
      setAdvanceSearchLoadingStates(false)
    }
  }

  const buildCityOptions = (stateCode: string, cityMapOverride?: Record<string, string[]>) => {
    const map = cityMapOverride ?? advanceSearchCityMap
    const raw = (() => {
      if (stateCode) return map[stateCode] ?? []
      const all: string[] = []
      for (const cities of Object.values(map)) {
        if (!Array.isArray(cities)) continue
        all.push(...cities)
      }
      return Array.from(new Set(all)).sort((a, b) => a.localeCompare(b))
    })()
    const cleaned = dropJunkCityNames(raw)
    const opts = cleaned.map((c) => ({ value: c, label: c }))
    setAdvanceSearchCities(opts)
    if (advanceSearchCity && !cleaned.includes(advanceSearchCity)) {
      setAdvanceSearchCity('')
    }
  }

  const loadAdvanceSearchProperties = async (params: { state?: string; city?: string }) => {
    setAdvanceSearchLoadingProperties(true)
    setAdvanceSearchProperties([])
    try {
      const rows = await getPropertiesByStateCity(params)
      const opts: SearchableSelectOption[] = rows.map((p) => ({
        value: String(p.property_id),
        label: String(p.property_name ?? ''),
        data: p as PropertyTabPropertyRecord,
      }))
      setAdvanceSearchProperties(opts)
      if (opts.length === 1) {
        setAdvanceSearchSelectedId(String(opts[0].value))
      }
    } catch (e) {
      showError((e as { message?: string })?.message || 'Failed to fetch properties.')
      setAdvanceSearchProperties([])
    } finally {
      setAdvanceSearchLoadingProperties(false)
    }
  }

  const handleOpenAdvanceSearch = () => {
    setIsAdvanceSearchOpen(true)
    setAdvanceSearchSelectedId('')
    setAdvanceSearchState('')
    setAdvanceSearchCity('')
    setAdvanceSearchProperties([])
    void loadAdvanceSearchReferenceData().then((cityMap) => {
      buildCityOptions('', cityMap)
      void loadAdvanceSearchProperties({})
    })
  }

  const handleApplyAdvanceSearch = () => {
    if (!advanceSearchSelectedId) return
    const selectedFromModal = advanceSearchProperties.find(
      (o) => String(o.value) === String(advanceSearchSelectedId),
    )
    if (selectedFromModal) {
      setPropertyOptions((prev) => {
        if (prev.some((o) => String(o.value) === String(selectedFromModal.value))) {
          return prev
        }
        return [selectedFromModal, ...prev]
      })
      if (selectedFromModal.data) {
        setSelectedPropertyData(selectedFromModal.data)
      }
    }
    applySelectedProperty(advanceSearchSelectedId, {
      data: selectedFromModal?.data as PropertyTabPropertyRecord | undefined,
    })
    setIsAdvanceSearchOpen(false)
  }

  const handleAdvanceSearchStateChange = (next: string) => {
    setAdvanceSearchState(next)
    setAdvanceSearchCity('')
    setAdvanceSearchSelectedId('')
    buildCityOptions(next)
    void loadAdvanceSearchProperties({
      state: next || undefined,
      city: undefined,
    })
  }

  const handleAdvanceSearchCityChange = (next: string) => {
    setAdvanceSearchCity(next)
    setAdvanceSearchSelectedId('')
    void loadAdvanceSearchProperties({
      state: advanceSearchState || undefined,
      city: next || undefined,
    })
  }

  return {
    isAdvanceSearchOpen,
    setIsAdvanceSearchOpen,
    advanceSearchSelectedId,
    setAdvanceSearchSelectedId,
    advanceSearchState,
    advanceSearchCity,
    advanceSearchStates,
    advanceSearchCities,
    advanceSearchProperties,
    advanceSearchLoadingStates,
    advanceSearchLoadingProperties,
    handleOpenAdvanceSearch,
    handleApplyAdvanceSearch,
    handleAdvanceSearchStateChange,
    handleAdvanceSearchCityChange,
  }
}
